import React from "react";

import { differenceInHours } from "date-fns";
import { Box, Stack, Tooltip, Typography } from "@mui/material";

import Sparkline from "./sparkline";

interface CostPanelProps {
  pennies: number;
  createdAt: string | Date;
}

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const labelSx = {
  fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
};

const valueSx = {
  fontFamily: "'IBM Plex Serif', ui-serif, Georgia, serif",
  fontSize: 28,
  fontWeight: 500,
  lineHeight: 1.1,
  letterSpacing: "-0.02em",
  color: "text.primary",
};

// Burn-rate panel for the single-cluster header. Shows current spend and a
// 30-day projection, with a sparkline stand-in for hourly burn (real metrics
// can wire in later — interface is just an array of numbers).
const CostPanel: React.FC<CostPanelProps> = ({ pennies, createdAt }) => {
  const hoursActive = Math.max(
    differenceInHours(new Date(), new Date(createdAt)) || 0,
    1,
  );
  const penniesPerHour = pennies / hoursActive;
  const projectedMonthly = penniesPerHour * 24 * 30;

  // Seeded deterministic burn-rate curve so the sparkline is stable across
  // re-renders but varied across clusters.
  const seed = `${createdAt}-${pennies}`;
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const series: number[] = [];
  for (let i = 0; i < 24; i += 1) {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    // Slight upward drift to suggest an hourly-burn trend.
    series.push(((h % 1000) / 1000) * 0.7 + i / 48);
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr auto" },
        gap: { xs: 2, sm: 3 },
        alignItems: "end",
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        backgroundColor: "var(--bg-sunken)",
      }}
    >
      <Tooltip title="Current cost based on actual usage since creation">
        <Stack spacing={0.5}>
          <Typography sx={labelSx}>Current spend</Typography>
          <Typography sx={valueSx}>
            {formatter.format(pennies / 100)}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
              fontSize: 11,
              color: "text.secondary",
            }}
          >
            {formatter.format(penniesPerHour / 100)}/hr · {hoursActive}h active
          </Typography>
        </Stack>
      </Tooltip>

      <Tooltip title="Projected monthly cost at the current burn rate">
        <Stack spacing={0.5}>
          <Typography sx={labelSx}>Projected / month</Typography>
          <Typography sx={{ ...valueSx, color: "var(--warn)" }}>
            {formatter.format(projectedMonthly / 100)}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
              fontSize: 11,
              color: "text.secondary",
            }}
          >
            at current burn rate
          </Typography>
        </Stack>
      </Tooltip>

      <Box
        sx={{
          gridColumn: { xs: "1 / -1", sm: "auto" },
          minWidth: { xs: "100%", sm: 180 },
          alignSelf: "stretch",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ ...labelSx, mb: 0.5 }}>burn rate</Typography>
        <Sparkline
          values={series}
          stroke="var(--accent)"
          fill="var(--accent-soft)"
          height={40}
        />
      </Box>
    </Box>
  );
};

export default CostPanel;
